'use client'
import React from 'react';
import * as Headless from '@headlessui/react'
import { AnimatePresence, motion } from 'motion/react'; // Updated import
import clsx from 'clsx'

import { Button } from '@/components/reusable/button'
import { Link } from 'gatsby';

/**
 * Main Dropdown container component
 * Wraps headlessUI Menu component to provide dropdown functionality
 */
export function Dropdown({ children, ...props }) {
  return (
    <AnimatePresence>
      <Headless.Menu {...props}>
        {children}
      </Headless.Menu>
    </AnimatePresence>
  )
}

/**
 * Button that toggles the dropdown menu
 * @param {Component} as - Component to render as the button (defaults to Button)
 */
export function DropdownButton({ as = Button, className, ...props }) {
  return <Headless.MenuButton as={as} className={clsx("cursor-pointer", className)} {...props} />
}

/**
 * Container for dropdown menu items with animation
 * @param {string} anchor - Position of the dropdown relative to its trigger (e.g., 'bottom end')
 */
export function DropdownMenu({ anchor = 'bottom end', className, ...props }) {
  return (
    <Headless.Menu.Items
      {...props}
      as={motion.div}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: {
          duration: 0.1,
        }
      }}
      anchor={anchor}
      className={clsx(
        // Anchor positioning
        '[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)] data-[anchor~=start]:[--anchor-offset:-6px] data-[anchor~=end]:[--anchor-offset:6px] sm:data-[anchor~=start]:[--anchor-offset:-4px] sm:data-[anchor~=end]:[--anchor-offset:4px]',
        // Base styles
        'isolate w-max rounded-lg p-2',
        // Z-index to ensure dropdown appears above dialogs (which use z-10)
        'z-50',
        // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
        'outline-solid outline-1 outline-transparent focus:outline-hidden',
        // Handle scrolling when menu won't fit in viewport
        'overflow-y-auto',
        // Popover background
        'bg-white/95 dark:bg-zinc-800/95 backdrop-blur-xl',
        // Shadows
        'shadow-lg ring-1 ring-zinc-950/5 dark:ring-inset dark:ring-zinc-100/5',
        // Define grid at the menu level if subgrid is supported
        'supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
        // Consumer overrides (last so they win in Tailwind v4)
        className,
      )}
    />

  )
}

/**
 * Wrapper component to prevent Gatsby Link's 'current' prop from reaching the DOM
 * This prevents React warnings about non-boolean attribute values
 */
const SafeLink = React.forwardRef(function SafeLink({ current: _current, ...props }, ref) {
  // Filter out 'current' prop that Gatsby Link passes to indicate active state
  // This prevents the React warning about passing boolean to non-boolean DOM attribute
  return <Link ref={ref} {...props} />;
});

/**
 * Individual dropdown menu item
 *
 * Renders as one of three element types based on the props passed:
 * - `to` (internal route)   → Gatsby `<Link>` for client-side nav
 * - `href` (external URL)   → `<a>` so the browser actually navigates;
 *                             callers pass `target` / `rel` and they
 *                             flow through via the rest spread
 * - neither                 → `<button type="button">` for action items
 *                             that fire an `onClick` handler
 *
 * Without the `href` branch, external-link call-sites would silently
 * fall through to `<button>` and the link wouldn't work — `target`/`rel`
 * spread onto a button is meaningless.
 */
export function DropdownItem({ className, to, href, ...props }) {
  let classes = clsx(
    className,
    // Base styles
    'group cursor-pointer rounded-lg focus:outline-hidden px-2 py-2',
    // Text styles
    'text-left text-sm/6 text-black dark:text-zinc-100 forced-colors:text-[CanvasText]',
    // Focus
    'data-focus:bg-black/5 dark:data-focus:bg-zinc-100/5 data-focus:text-black dark:data-focus:text-zinc-100',
    // Disabled state
    'data-disabled:opacity-50',
    // Forced colors mode
    'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText] forced-colors:data-focus:*:data-[slot=icon]:text-[HighlightText]',
    // Use subgrid when available but fallback to an explicit grid layout if not
    'col-span-full flex items-center gap-2',
  )

  if (to) {
    return <Headless.MenuItem as={SafeLink} to={to} {...props} className={classes} />
  }
  if (href) {
    return <Headless.MenuItem as="a" href={href} {...props} className={classes} />
  }
  return <Headless.MenuItem as="button" type="button" {...props} className={classes} />
}

/**
 * Groups related dropdown items together
 */
export function DropdownSection({ className, ...props }) {
  return (
    <Headless.MenuSection
      {...props}
      className={clsx(
        className,
        'flex flex-col'
      )}
    />
  )
}

/**
 * Visual separator between dropdown sections
 */
export function DropdownDivider({ className, ...props }) {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(
        className,
        'col-span-full my-2 h-px border-0 bg-zinc-950/5 dark:bg-zinc-100/10 forced-colors:bg-[CanvasText]'
      )}
    />
  )
}

/**
 * Text label for dropdown sections
 */
export function DropdownLabel({ className, ...props }) {
  return (
    <Headless.Label {...props} data-slot="label" className={clsx(className, 'col-start-2 row-start-1')} {...props} />
  )
}

export function DropdownHeading({ className, ...props }) {
  return (
    <h5
      {...props}
      className={clsx(
        className,
        "block truncate text-xs/5 font-medium text-primary p-2"
      )}
    />
  );
}